import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Copy, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Section, Eyebrow, GlassCard } from "../components/ui-shared";
import { Field, TextArea, Select } from "../components/FormField";

const CATEGORIES = ["Fashion & Style", "Beauty", "Food & Cooking", "Travel", "Fitness & Health", "Tech & Gadgets", "Gaming", "Comedy & Entertainment", "Lifestyle", "Finance", "Education", "Other"];
const CONTENT_TYPES = ["Reels / Short-form video", "Long-form video", "Photo / Carousel", "Stories", "Mixed"];

const requiredFields = ["full_name", "email", "phone", "instagram", "city", "creator_category", "followers", "average_reel_views", "engagement_rate", "content_type", "collaboration_rate", "motivation"];

export default function CreatorApply() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", instagram: "", city: "", creator_category: "",
    followers: "", average_reel_views: "", engagement_rate: "", portfolio_link: "",
    content_type: "", collaboration_rate: "", motivation: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    requiredFields.forEach((k) => { if (!form[k].trim()) errs[k] = "Required"; });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fill in all required fields."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/applications/creator", form);
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Section className="min-h-[70vh]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <GlassCard hover={false} data-testid="success-confirmation-card" className="max-w-xl mx-auto p-8 sm:p-12 text-center">
            <span className="grid place-items-center h-16 w-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/25 text-emerald-300">
              <CheckCircle2 size={30} />
            </span>
            <h1 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-white">Application received.</h1>
            <p className="mt-3 text-slate-400">We're reviewing applications for the initial creator network.</p>
            <div className="mt-6 flex items-center justify-center gap-2 font-mono text-sm">
              <span className="text-slate-500">Reference:</span>
              <span className="text-purple-300">{result.id}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(result.id); toast.success("Reference copied"); }}
                className="text-slate-400 hover:text-white" data-testid="copy-reference-button" aria-label="Copy reference"
              ><Copy size={14} /></button>
            </div>
            <p className="mt-6 text-xs text-slate-500">Submitting an application does not guarantee Private Beta access or campaign opportunities.</p>
            <Link to="/" className="btn-ghost mt-8">Back to Home</Link>
          </GlassCard>
        </motion.div>
      </Section>
    );
  }

  return (
    <Section>
      <div className="max-w-2xl mx-auto">
        <Eyebrow>Creator Private Beta</Eyebrow>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">APPLY FOR CREATOR PRIVATE BETA</h1>
        <p className="mt-3 text-slate-400">Share your profile so businesses can discover you for relevant campaigns.</p>

        <form onSubmit={submit} data-testid="creator-form-container" className="mt-10 glass rounded-2xl p-6 sm:p-8 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required error={errors.full_name} />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required error={errors.email} />
            <Field label="Phone / WhatsApp" name="phone" value={form.phone} onChange={onChange} required error={errors.phone} />
            <Field label="Instagram Handle" name="instagram" value={form.instagram} onChange={onChange} placeholder="@yourhandle" required error={errors.instagram} />
            <Field label="City" name="city" value={form.city} onChange={onChange} required error={errors.city} />
            <Select label="Creator Category" name="creator_category" value={form.creator_category} onChange={onChange} options={CATEGORIES} required error={errors.creator_category} />
            <Field label="Followers" name="followers" value={form.followers} onChange={onChange} placeholder="e.g. 45,000" required error={errors.followers} />
            <Field label="Average Reel Views" name="average_reel_views" value={form.average_reel_views} onChange={onChange} placeholder="e.g. 20,000" required error={errors.average_reel_views} />
            <Field label="Engagement Rate" name="engagement_rate" value={form.engagement_rate} onChange={onChange} placeholder="e.g. 4.5%" required error={errors.engagement_rate} />
            <Select label="Content Type" name="content_type" value={form.content_type} onChange={onChange} options={CONTENT_TYPES} required error={errors.content_type} />
            <Field label="Typical Collaboration Rate" name="collaboration_rate" value={form.collaboration_rate} onChange={onChange} placeholder="e.g. ₹15,000 / reel" required error={errors.collaboration_rate} />
            <Field label="Portfolio / Content Link" name="portfolio_link" value={form.portfolio_link} onChange={onChange} placeholder="Optional" />
          </div>
          <TextArea label="Why would you like to join Neo Orbit?" name="motivation" value={form.motivation} onChange={onChange} required error={errors.motivation} />

          <button type="submit" disabled={submitting} data-testid="creator-form-submit-button" className="btn-primary w-full !mt-2 disabled:opacity-60">
            {submitting ? "Submitting…" : <>Submit Application <ArrowRight size={18} /></>}
          </button>
          <p className="text-xs text-slate-500 text-center">Submitting an application does not guarantee Private Beta access or campaign opportunities.</p>
        </form>
      </div>
    </Section>
  );
}
